import AddIcon from '@mui/icons-material/Add';
import Fab from '@mui/material/Fab';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PerilConfigurationResponse } from '../../../api/ConfigApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import { getNewPeril, PolicyData } from '../../../context/policy';
import { Exposure, PerilCreateRequest } from '../../../types/policy';
import { sortByObjectKey } from '../../../util/util';
import { StyledModalContentContainer } from '../../common/index';
import DefaultDialog from '../../DefaultDialog';
import { errorToast, successToast } from '../../Toast';
import ReactSelect, { SelectValue } from '../fields/ReactSelect';

interface Props {
	perilConfigs: any[];
	exposureLocator: string;
	policyData: PolicyData;
	authData: AuthData;
}

export default function AddPerilModal(props: Props) {
	const { t } = useTranslation();
	const { perilConfigs, exposureLocator, policyData, authData } = props;
	const { saveFastForm, setLoading } = policyData;
	const params = useParams();
	const navigate = useNavigate();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;

	const sortedPerils = sortByObjectKey(perilConfigs, 'displayName');
	const [isOpen, setIsOpen] = useState(false);
	const initialValue = sortedPerils[0]
		? { value: sortedPerils[0].name, label: sortedPerils[0].displayName }
		: { value: '', label: '' };
	const [selectedValue, setSelectedValue] = useState(initialValue);
	const close = () => setIsOpen(false);
	const open = () => setIsOpen(true);
	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const addPeril = async () => {
		await close();
		setLoading(true);
		const perilConfig: PerilConfigurationResponse = perilConfigs.find(
			(config) => config.name === selectedValue.value,
		);

		const newPeril: PerilCreateRequest = getNewPeril(
			perilConfig,
			hidePerilLimitsEnabled,
		);

		try {
			const updatedPolicy = await saveFastForm(getValues, dirtyFields, reset, {
				addPeril: { exposureLocator, peril: newPeril },
			});
			// navigate to newly created peril
			const updatedExposure: Exposure = updatedPolicy?.policy?.exposures?.find(
				(exp: Exposure) => exp.displayId === params.exposureId,
			);

			if (updatedExposure) {
				const createdPerilId =
					updatedExposure.perils[updatedExposure.perils.length - 1]?.displayId;
				if (createdPerilId) {
					successToast('Successfully added peril');
					navigate(
						`/policy/${params.policyId}/quotes/${params.quoteId}/exposures/${
							params.exposureId
						}/perils/${createdPerilId ? createdPerilId : ''}`,
					);
				}
			}
		} catch (error) {
			errorToast('Error adding peril');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const perilOptions = sortedPerils.map((pC: PerilConfigurationResponse) => ({
		value: pC.name,
		label: pC.displayName,
	}));

	return (
		<>
			<Fab
				color='primary'
				onClick={open}
				sx={{ position: 'fixed', bottom: 20, right: 20 }}
				variant='extended'
			>
				<AddIcon sx={{ mr: 1 }} /> {`Add ${t('peril.singular.upper')}`}
			</Fab>
			<DefaultDialog
				actions={[
					{ text: 'Add', onClick: addPeril },
					{ text: 'Cancel', onClick: close },
				]}
				open={isOpen}
				onClose={close}
				title={`Add ${t('peril.singular.upper')}`}
			>
				<StyledModalContentContainer>
					<div style={{ width: '50%', height: '40px' }}>
						<ReactSelect
							id='add-peril-selector'
							options={perilOptions}
							value={selectedValue}
							onChange={(opt: SelectValue) => setSelectedValue(opt)}
							disableOpenMenuOnFocus
						/>
					</div>
				</StyledModalContentContainer>
			</DefaultDialog>
		</>
	);
}
